/**
 * Platform-default brand: the single source of truth on the web side.
 *
 * These values mirror the system-layer `brand` configuration seeded by the API
 * (api-service/app/db/seed/sc/configuration_catalogue_seed.py). A tenant's saved brand config overrides
 * them at runtime; they are used as-is when signed out or when a tenant has not customised a colour.
 * Never copy these hex values into components: import DEFAULT_BRAND or, better, use the theme tokens.
 */
export const DEFAULT_BRAND = {
  /** Primary actions / CTAs. RoofVox Vibrant Orange. */
  primaryColor: '#F77623',
  /** AI, focus, selection and related accents, and the dark chrome gradient's second stop. RoofVox Steel Blue. */
  accentColor: '#4A6B80',
  successColor: '#10B981',
  /** Dark canvas the surface ramp is built from. RoofVox Brand Navy. Internal input: no tenant UI field. */
  baseColor: '#0C1824',
} as const;

/**
 * Fixed neutrals from the RoofVox brand guidelines. buildTheme() returns these EXACT values when the
 * base colour is the default Brand Navy, and derives an equivalent ramp when a tenant overrides it.
 */
export const ROOFVOX_NEUTRALS = {
  navy: '#0C1824',
  steel: '#4A6B80',
  surface2: '#1A2A3A',
  surface3: '#1E2E40',
  text1: '#F0F4F8',
  text2: '#A8BCC8',
  textMuted: '#6B8499',
} as const;

/**
 * Semantic status colours that are not tenant-configurable (`success` is, via brand config).
 * Both already exist in the app: amber is the de-facto warning colour, and danger is the historical
 * `#CF4148` literal that was written by hand ~170 times.
 */
export const STATUS_BASE = {
  warning: '#F59E0B',
  danger: '#CF4148',
} as const;

/**
 * Lowest contrast at which a WHITE label is still used on a primary-coloured fill. The brand specifies
 * white on Vibrant Orange (2.77:1), so the bar sits just under that; a lighter tenant primary falls
 * back to the base navy for its label.
 */
export const WHITE_LABEL_MIN_CONTRAST = 2.7;
